import { useState } from 'react';
import { Plus, Users2 } from 'lucide-react';
import { useTeams } from '@/api/teams';
import { useAllUsers } from '@/api/users';
import type { Team } from '@/api/types';
import { Avatar, Badge, Button, Card, EmptyState, ErrorState, PageHeader, Spinner } from '@/components/ui';
import { useAuth } from '@/app/auth/useAuth';
import { humanize } from '@/lib/format';
import { TeamFormModal } from './TeamFormModal';
import { TeamMembersModal } from './TeamMembersModal';
import { STRATEGY_HELP } from './helpers';

export function TeamsPage() {
  const { can } = useAuth();
  const canManage = can('manage', 'Team');
  const teams = useTeams();
  const users = useAllUsers();
  const [editing, setEditing] = useState<Team | 'new' | null>(null);
  const [members, setMembers] = useState<Team | null>(null);
  const userById = new Map((users.data ?? []).map((u) => [u.id, u]));

  return (
    <>
      <PageHeader
        title="Teams"
        description="Teams own queues. Each team picks how tickets are assigned to its members."
        actions={
          canManage && (
            <Button onClick={() => setEditing('new')}>
              <Plus className="h-4 w-4" /> New team
            </Button>
          )
        }
      />

      {teams.isPending ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" label="Loading teams" />
        </div>
      ) : teams.error ? (
        <Card>
          <ErrorState error={teams.error} onRetry={() => void teams.refetch()} />
        </Card>
      ) : teams.data.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Users2 className="h-6 w-6" />}
            title="No teams yet"
            description="Create teams like “Payments & Billing” or “Creator Success” to route tickets."
            action={canManage && <Button onClick={() => setEditing('new')}>Create a team</Button>}
          />
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {teams.data.map((t) => {
            const supervisor = t.supervisorId ? userById.get(t.supervisorId) : undefined;
            return (
              <Card key={t.id} className="flex flex-col p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="truncate text-base font-bold">{t.name}</h2>
                    <p className="font-mono text-xs text-slate-400">{t.slug}</p>
                  </div>
                  {!t.isActive && <Badge tone="red">Inactive</Badge>}
                </div>
                {t.description && <p className="mt-2 line-clamp-2 text-sm text-slate-600">{t.description}</p>}
                <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="text-xs text-slate-500">Assignment</dt>
                    <dd className="font-medium text-slate-800" title={STRATEGY_HELP[t.assignmentStrategy]}>
                      {humanize(t.assignmentStrategy)}
                      {!t.autoAssign && <span className="text-slate-400"> · paused</span>}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500">Auto-reassign</dt>
                    <dd className="font-medium text-slate-800">
                      {t.autoReassignMinutes ? `after ${t.autoReassignMinutes} min` : 'Off'}
                    </dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-xs text-slate-500">Supervisor</dt>
                    <dd className="mt-1 flex items-center gap-2 font-medium text-slate-800">
                      {supervisor ? (
                        <>
                          <Avatar name={supervisor.name} size="sm" status={supervisor.status} />{' '}
                          {supervisor.name}
                        </>
                      ) : (
                        <span className="text-slate-400">None</span>
                      )}
                    </dd>
                  </div>
                </dl>
                <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4">
                  <button
                    type="button"
                    onClick={() => setMembers(t)}
                    className="text-brand-600 hover:text-brand-700 text-sm font-medium"
                  >
                    {t.memberCount} {t.memberCount === 1 ? 'member' : 'members'} →
                  </button>
                  {canManage && (
                    <Button variant="ghost" size="sm" onClick={() => setEditing(t)}>
                      Edit
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {editing && (
        <TeamFormModal
          team={editing === 'new' ? null : editing}
          users={users.data ?? []}
          onClose={() => setEditing(null)}
        />
      )}
      {members && (
        <TeamMembersModal
          team={members}
          allUsers={users.data ?? []}
          canManage={canManage}
          onClose={() => setMembers(null)}
        />
      )}
    </>
  );
}
